import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import { NotifyReviewSubmissionSchema, REVIEW_NOTE_MAX, type TNotifyReviewSubmission } from '@onesource/schemas';
import { useNotifyReviewSubmission } from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose, MdOutlineMail, MdSend } from 'react-icons/md';

interface NotifySubmissionDialogProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  submissionId: string;
  formTitle: string;
  studentName: string;
  /** Null while the submission has no teacher — only the student can be noted then. */
  teacherName: string | null;
}

/**
 * Admin: write a note about this submission to its teacher or its student. They
 * get it in-app and by email; the link opens the submission (teacher) or the
 * form page (student).
 */
export const NotifySubmissionDialog: FC<NotifySubmissionDialogProps> = ({
  open,
  onClose,
  spaceId,
  submissionId,
  formTitle,
  studentName,
  teacherName,
}) => {
  const notify = useNotifyReviewSubmission();
  const { control, handleSubmit, reset, watch } = useForm<TNotifyReviewSubmission>({
    resolver: zodResolver(NotifyReviewSubmissionSchema),
    defaultValues: { recipient: teacherName ? 'teacher' : 'student', note: '' },
  });
  const recipient = watch('recipient');
  const note = watch('note');

  useEffect(() => {
    if (open) {
      reset({ recipient: teacherName ? 'teacher' : 'student', note: '' });
      notify.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: TNotifyReviewSubmission) => {
    try {
      const { sentTo } = await notify.mutateAsync({ spaceId, submissionId, ...values });
      enqueueSnackbar(`Note sent to ${sentTo.name}`, { variant: 'success' });
      onClose();
    } catch {
      // Shown in the dialog.
    }
  };

  const recipientName = recipient === 'teacher' ? teacherName : studentName;

  return (
    <Dialog open={open} onClose={notify.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose} icon={<MdOutlineMail size={20} />} subtitle={`${studentName} · ${formTitle}`}>
        Send a note
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
            Send to
          </Typography>
          <Controller
            name="recipient"
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={field.value}
                onChange={(_, next) => next && field.onChange(next)}
                aria-label="Who gets the note"
                sx={{ mb: 1, '& .MuiToggleButton-root': { textTransform: 'none', flexDirection: 'column', py: 1 } }}
              >
                <ToggleButton value="teacher" disabled={!teacherName}>
                  <strong>Teacher</strong>
                  <Typography variant="caption" color="text.secondary">
                    {teacherName ?? 'No teacher assigned'}
                  </Typography>
                </ToggleButton>
                <ToggleButton value="student">
                  <strong>Student</strong>
                  <Typography variant="caption" color="text.secondary">
                    {studentName}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {recipient === 'teacher'
              ? `${teacherName ?? 'The teacher'} gets it in-app and by email, with a link that opens this submission.`
              : `${studentName} gets it in-app and by email, with a link that opens their ${formTitle} form.`}
          </Typography>
          <Controller
            name="note"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                autoFocus
                multiline
                minRows={4}
                fullWidth
                label={`Note to ${recipientName ?? 'them'}`}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? `${note.length}/${REVIEW_NOTE_MAX}`}
              />
            )}
          />
          {notify.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {notify.error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="soft" startIcon={<MdClose />} onClick={onClose} disabled={notify.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={notify.isPending}
            startIcon={notify.isPending ? <CircularProgress size={16} color="inherit" /> : <MdSend />}
          >
            Send note
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
